/**
 * Error Boundary component
 * Catches React errors and displays user-friendly error UI
 * Integrates with Sentry for error reporting
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { captureException } from '../lib/sentry';
import { isDevelopment } from '../lib/env';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to Sentry in production
    captureException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });

    this.setState({
      error,
      errorInfo,
    });

    if (isDevelopment()) {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI with Matrix theme
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="matrix-container max-w-2xl w-full p-8">
            <div className="text-center space-y-6">
              {/* Error Icon */}
              <div className="text-red-500 text-6xl font-mono">
                [ ERROR ]
              </div>

              {/* Error Message */}
              <div className="text-green-400 font-mono">
                <h1 className="text-2xl mb-4">System Malfunction Detected</h1>
                <p className="text-sm opacity-70 mb-2">
                  The matrix has encountered an unexpected error
                </p>
                {isDevelopment() && this.state.error && (
                  <div className="mt-4 p-4 bg-red-900/20 border border-red-500 rounded text-left">
                    <p className="text-red-400 text-xs mb-2">
                      <strong>Error:</strong> {this.state.error.message}
                    </p>
                    {this.state.error.stack && (
                      <pre className="text-red-300 text-xs overflow-auto max-h-40">
                        {this.state.error.stack}
                      </pre>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={this.handleReload}
                  className="px-6 py-3 bg-green-600 hover:bg-green-500 text-black font-mono font-bold rounded transition-colors min-w-[160px]"
                >
                  [ RELOAD ]
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-mono font-bold rounded transition-colors min-w-[160px]"
                >
                  [ GO HOME ]
                </button>
                <button
                  onClick={this.handleReset}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-green-400 font-mono font-bold rounded transition-colors min-w-[160px]"
                >
                  [ RETRY ]
                </button>
              </div>

              {/* Additional Info */}
              <div className="text-green-500/50 text-xs font-mono mt-8">
                <p>ERROR CODE: {Date.now()}</p>
                <p className="mt-2">
                  If this persists, contact support with the error code above
                </p>
              </div>
            </div>

            {/* CRT scanline effect */}
            <div className="absolute inset-0 pointer-events-none scanlines opacity-10"></div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
